import { selMessage } from "@selector/shared";
import { differenceBy, remove } from "lodash-es";
import pluralize from "pluralize";
import { Component } from "react";
import { connect } from "react-redux";
import { type ActionMeta, type ValueType } from "react-select";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { CalloutIntent, Copyable } from "widgets";
import { objectDifferenceLeft } from "widgets/utils";

import { sanitizeFilename } from "@/app-utils/export.tsx";
import { type ISelectOption, simpleDropdownFieldValidation, simpleFieldValidation } from "@/app-utils/form.ts";
import { capitalizeFirstLetterOfAllWords, pluralizeWord } from "@/app-utils/string.ts";
import { fileNameFieldValidation } from "@/app-utils/validation.ts";
import {
  type Contig,
  type InputRegionInput,
  type InputTableQuerySpec,
  type Organism,
  type Range,
  type SequenceFeatureTypes,
} from "@/graphql.ts";
import { exportItemsOrRegion, type IDataExportInput, type IGenericExportPayload } from "@/redux/modules/data-export.ts";
import { checkSequencedOrganisms, type IGenomeState, loadGenomeGffFeatureStatistics } from "@/redux/modules/genome.ts";
import { type IGenomeBrowserState } from "@/redux/modules/genome-browser.ts";
import { type IJobState } from "@/redux/modules/job.ts";
import { getTableQuerySpecWithSelection, type ITableData } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import {
  BLAST_METHOD,
  ENTITY_TYPE,
  EXPORT_FILE_FORMATS,
  FASTA_HEADER_TEMPLATE_OPTIONS,
  FASTA_HEADER_TEMPLATE_OPTIONS_DEFAULT,
  FEATURE_DISPLAY_NAME,
  getSequenceFeatureTypeDisplayName,
  GFF_FEATURE_TYPE_AA_FEATURE,
  GFF_FEATURE_TYPE_CDS,
  GFF_FEATURE_TYPE_EXON,
  GFF_FEATURE_TYPE_GENE,
  GFF_FEATURE_TYPE_LNCRNA,
  GFF_FEATURE_TYPE_MRNA,
  GFF_FEATURE_TYPE_NCRNA,
  GFF_FEATURE_TYPE_PSEUDOGENE,
  GFF_FEATURE_TYPE_RRNA,
  GFF_FEATURE_TYPE_SNORNA,
  GFF_FEATURE_TYPE_SNRNA,
  GFF_FEATURE_TYPE_TRNA,
  GFF_FEATURE_TYPES,
  GFF_TO_BROWSER_FEATURE_TYPE,
  GFF_TO_EXPORT_TRACK_TYPE,
  MAX_SEQUENCE_EXPORT_LENGTH,
  STRAND_TYPE,
  VIEW,
} from "@/settings/settings.ts";
import { Addon } from "@/ui-modules/core/redux-form/Addon.js";

import { notify } from "../../modules/notifications/notify.js";
import { Dialog } from "./overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "./redux-form/CheckboxField.js";
import { CheckboxSelectReduxFormField, default as CheckboxSelectField } from "./redux-form/CheckboxSelectField.js";
import InputField, { InputReduxFormField } from "./redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "./redux-form/ReactSelectField.js";
import { SelectionType } from "./save-dialog/utils.js";

interface IFastaCheck {
  all: boolean;
  count: number;
}

export interface IGffTrackType {
  trackType: string;
  subTypeId?: string;
  name: string;
}

export interface IGffPayload {
  format: string;
  genome: Organism;
  trackTypes: IGffTrackType[];
  name: string;
  withSequence: boolean;
  useChromosomeName: boolean;
  compressed: boolean;
  reverseComplement: boolean;
  region?: InputRegionInput;
}

export interface IVcfPayload extends IDataExportInput {
  genome: Organism;
}

export interface ITableExportDialogOwnProps {
  viewType: string;
  tables: ITableData[];
  summaryPageEntity: { id: number; groupType: string };
  close?: () => void;
  getParentEntityName?: () => string;
}

enum EXPORT_ACTION {
  TO_FILE,
  TO_CLIPBOARD,
}

export interface ITableExportDialogSelectOption {
  value: ITableExportDialogSelectOptionValue;
  label: string;
}

interface ITableExportDialogSelectOptionValue {
  selectionType: number;
  tableData?: ITableData;
  itemCount?: number;
}

interface ITableExportDialogState {
  sourceOptions: ITableExportDialogSelectOption[];
  formatOptions: ISelectOption[];
  allFeatureOptions: IGffTrackType[];
  featureOptions: IGffTrackType[];
  action: EXPORT_ACTION;
  warning: string;
  error: string;
  withSequence: boolean;
}

interface ITableExportDialogAppStateProps {
  genomeBrowser: IGenomeBrowserState;
  currentContentSelection: ITableExportDialogSelectOption;
  currentFormatSelection: ISelectOption;
  currentGffFeatureSelection: IGffTrackType[];
  currentExportCompressed: boolean;
  currentExportReverseComplement: boolean;
  initialValues: Partial<any>;
  genome: IGenomeState;
  job: IJobState;
  sequenceFeatureTypes: SequenceFeatureTypes;
  exportedEntityType: string;
  getContent: (() => Promise<string | null>) | undefined;
  formValues: any;
}

interface ITableExportDialogDispatchProps {
  exportItemsOrRegion: (payload: IGenericExportPayload) => Promise<any>;
  loadGenomeGffFeatureStatistics: (genomeId: number) => Promise<Organism>;
  checkSequencedOrganisms: (tableQuerySpec: InputTableQuerySpec) => Promise<boolean>;
}

export interface ITableExportDialogProps
  extends ITableExportDialogAppStateProps,
    ITableExportDialogDispatchProps,
    ITableExportDialogOwnProps,
    InjectedFormProps<any, any> {}

const CHROMOSOMENAME: ISelectOption = { label: "Chromosome Name", value: 1 };
const CONTIG_ID: ISelectOption = { label: "Contig Accession ID", value: 0 };
const gffLandmarkNameOptions: ISelectOption[] = [CHROMOSOMENAME, CONTIG_ID];

function mapStateToProps(state: IAppState, _ownProps: ITableExportDialogProps): ITableExportDialogAppStateProps {
  const currentContentSelection = formValueSelector(TableExportDialog.FORM_NAME)(
    state,
    TableExportDialog.FIELD_EXPORT_SELECTOR,
  );

  // Caution: This is fragile logic which handles exotic situations like "visible region" only implicitly(by ignoring them and returning null).
  // There seems to be no generic way to detect the exported entity type in all situations because sometimes there isn't any.
  // We assume that in this case, a nucleic acid export will happen. This seems to work for all cases.
  const exportedEntityType: string = currentContentSelection
    ? currentContentSelection.value.tableData
      ? currentContentSelection.value.tableData.tableQuerySpec.resultType
      : this.props?.summaryPageEntity
        ? this.props?.summaryPageEntity.groupType
        : null
    : null;

  return {
    getContent: undefined,
    formValues: state.form?.[TableExportDialog.FORM_NAME]?.values,
    currentContentSelection,
    currentFormatSelection: formValueSelector(TableExportDialog.FORM_NAME)(
      state,
      TableExportDialog.FIELD_EXPORT_FORMAT,
    ),
    currentGffFeatureSelection: formValueSelector(TableExportDialog.FORM_NAME)(
      state,
      TableExportDialog.FIELD_GFF_FEATURE_TYPES,
    ),
    currentExportCompressed: formValueSelector(TableExportDialog.FORM_NAME)(
      state,
      TableExportDialog.FIELD_EXPORT_COMPRESSED,
    ),
    currentExportReverseComplement: formValueSelector(TableExportDialog.FORM_NAME)(
      state,
      TableExportDialog.FIELD_EXPORT_REVERSE_COMPLEMENT,
    ),
    genomeBrowser: state.genomeBrowser,
    genome: state.genome,
    job: state.job,
    initialValues: {
      [TableExportDialog.FIELD_EXPORT_FILENAME]: "",
      [TableExportDialog.FIELD_EXPORT_FASTA_HEADER]:
        FASTA_HEADER_TEMPLATE_OPTIONS[FASTA_HEADER_TEMPLATE_OPTIONS_DEFAULT],
      [TableExportDialog.FIELD_GFF_FEATURE_TYPES]: [],
      [TableExportDialog.FIELD_GFF_LANDMARK_NAME]: gffLandmarkNameOptions[0],
      [TableExportDialog.FIELD_EXPORT_COMPRESSED]: true,
      [TableExportDialog.FIELD_EXPORT_REVERSE_COMPLEMENT]: false,
    },
    sequenceFeatureTypes: state.listing.sequenceFeatureTypes,
    exportedEntityType,
  };
}

function mapDispatchToProps(dispatch): ITableExportDialogDispatchProps {
  return {
    exportItemsOrRegion: (payload: IGenericExportPayload) => dispatch(exportItemsOrRegion(payload)),
    loadGenomeGffFeatureStatistics: (genomeId: number) => dispatch(loadGenomeGffFeatureStatistics(genomeId)),
    checkSequencedOrganisms: (tableQuerySpec: InputTableQuerySpec) => dispatch(checkSequencedOrganisms(tableQuerySpec)),
  };
}

class TableExportDialog extends Component<ITableExportDialogProps, ITableExportDialogState> {
  public static FORM_NAME = "exportTable";

  public static FIELD_EXPORT_FORMAT = "exportFormat";
  public static FIELD_EXPORT_SELECTOR = "exportSelector";
  public static FIELD_EXPORT_FILENAME = "exportFileName";
  public static FIELD_EXPORT_FASTA_HEADER = "exportFastHeaderTemplate";
  public static FIELD_EXPORT_COMPRESSED = "exportCompressed";
  public static FIELD_EXPORT_REVERSE_COMPLEMENT = "exportReverseComplement";
  public static FIELD_GFF_FEATURE_TYPES = "exportGffFeatureTypes";
  public static FIELD_GFF_LANDMARK_NAME = "exportGffLandmarkName";
  public static FIELD_GFF_WITH_SEQUENCE = "exportGffWithSequence";

  private static EXPORT_WARNING_THRESHOLD = 10;

  state = {
    sourceOptions: [],
    formatOptions: [],
    allFeatureOptions: new Array<IGffTrackType>(),
    featureOptions: new Array<IGffTrackType>(),
    action: null,
    warning: null,
    error: null,
    withSequence: false,
    getContent: undefined,
    formValues: this.props.initialValues,
  };

  initExportOptions = () => {
    const formatOptions: ISelectOption[] = this.createFormatOptions();
    const currentFormatSelection: ISelectOption = this.props.currentFormatSelection;
    const currentFormatSelectionInvalid: boolean =
      !this.props.currentFormatSelection ||
      (formatOptions &&
        !formatOptions.find((f: ISelectOption) => {
          return f.value === this.props.currentFormatSelection.value;
        }));

    if (formatOptions && formatOptions[0] && currentFormatSelectionInvalid) {
      this.props.change(TableExportDialog.FIELD_EXPORT_FORMAT, formatOptions[0]);
    }

    const FASTAFormatSelected: boolean =
      currentFormatSelection && currentFormatSelection.value === EXPORT_FILE_FORMATS.FASTA_FORMAT;
    const currSelectionType: number =
      this.props.currentContentSelection && this.props.currentContentSelection.value.selectionType;
    if (
      FASTAFormatSelected &&
      (currSelectionType === SelectionType.SelectedRegion || currSelectionType === SelectionType.VisibleRegion)
    ) {
      this.props.change(
        TableExportDialog.FIELD_EXPORT_FASTA_HEADER,
        FASTA_HEADER_TEMPLATE_OPTIONS[FASTA_HEADER_TEMPLATE_OPTIONS_DEFAULT],
      );
    }
  };

  componentDidMount() {
    const defaultSourceOption: ITableExportDialogSelectOption = this.createSourceOptions();
    if (defaultSourceOption) {
      this.props.change(TableExportDialog.FIELD_EXPORT_SELECTOR, defaultSourceOption);
    }

    // this.isGffExportAvailable() is not sufficient in Genome Browser context
    // because it includes a content check which does not always apply at mount time
    if (this.isGffExportAvailable() || this.isGenomeBrowserContext()) {
      if (this.isGenomeBrowserContext()) {
        this.createAndSetFeatureTypeOptions();
      } else {
        // loading the feature statistics required for the gff export dialog
        void this.props.loadGenomeGffFeatureStatistics(this.props.genome.genome.id).then(() => {
          this.createAndSetFeatureTypeOptions();
        });
      }
    }

    this.initExportOptions();
    this.setDefaultFileName();
  }

  componentDidUpdate(prevProps: ITableExportDialogProps, _prevState) {
    if (
      this.props.exportedEntityType !== prevProps.exportedEntityType ||
      this.props.currentExportReverseComplement !== prevProps.currentExportReverseComplement
    ) {
      this.setDefaultFileName();
    }

    // the meaning of "CDS" count differs for GB and GFF exports and needs to be re-calculated
    if (
      this.props.currentFormatSelection !== prevProps.currentFormatSelection &&
      (this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GBK_FORMAT ||
        this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GFF_FORMAT)
    ) {
      this.createAndSetFeatureTypeOptions();
    }

    if (!this.props.currentContentSelection) {
      // in case the same export dialog component appears twice on the same page, it might happen that we close one by opening the other,
      // and in this case - since the redux form is the same - some form values might need to be set again, since the form unmounting does a reset
      const defaultSourceOption: ITableExportDialogSelectOption = this.createSourceOptions();
      if (defaultSourceOption) {
        this.props.change(TableExportDialog.FIELD_EXPORT_SELECTOR, defaultSourceOption);
      }
    }

    // re-create options because we might change the value labels ("Table"-"Row" instead of e.g. "Sequence"-"Sequence")
    if (this.props.currentFormatSelection !== prevProps.currentFormatSelection) {
      this.createSourceOptions();
    }

    // if the selected content changed, refresh the format options and init the redux form field on the first format option
    if (
      prevProps.currentContentSelection?.value?.selectionType !==
      this.props.currentContentSelection?.value?.selectionType
    ) {
      this.initExportOptions();
    }

    if (!this.props.summaryPageEntity && prevProps.currentFormatSelection !== this.props.currentFormatSelection) {
      this.checkExportTypeAndCount();
    }

    // make sure that gff feature type selections are always consistent
    if (
      this.props.currentGffFeatureSelection &&
      prevProps.currentGffFeatureSelection &&
      this.props.currentGffFeatureSelection.length !== prevProps.currentGffFeatureSelection.length &&
      this.props.currentFormatSelection?.value !== EXPORT_FILE_FORMATS.GBK_FORMAT
    ) {
      this.ensureGffFeatureConsistency(prevProps.currentGffFeatureSelection, this.props.currentGffFeatureSelection);
    }

    // Reset Copyable on every form inputs changes. A bit unoptimized as we react to everything.
    if (
      objectDifferenceLeft(this.props.formValues ?? {}, prevProps.formValues ?? {}, [
        TableExportDialog.FIELD_EXPORT_FORMAT,
        TableExportDialog.FIELD_EXPORT_FASTA_HEADER,
        TableExportDialog.FIELD_EXPORT_REVERSE_COMPLEMENT,
        TableExportDialog.FIELD_EXPORT_SELECTOR,
      ]).length
    ) {
      this.setState({
        // getContent: () => this.handleExportToClipboard(this.props.formValues), // <- continuous re-render
        getContent: () => this.handleExport(this.props.formValues, true),
      });
    }
  }

  private createAndSetFeatureTypeOptions() {
    const gffFeatureOptions: IGffTrackType[] = this.createGffFeatureOptions();
    this.updateFeatureTypeOptions(this.props.currentFormatSelection?.value, gffFeatureOptions.slice(0));
  }

  private setDefaultFileName() {
    // extract the genome name from the applicable place
    let entityName: string = this.isGenomeBrowserContext()
      ? this.props.genomeBrowser?.genome?.name
      : this.props.getParentEntityName?.();

    // create entity-specific suffixes, working both for subtypes and normal entities
    const typeDisplayName: string = getSequenceFeatureTypeDisplayName(
      this.props.currentContentSelection?.value?.tableData?.tableQuerySpec,
      this.props.sequenceFeatureTypes,
    );

    let suffix: string = (typeDisplayName || FEATURE_DISPLAY_NAME[this.props.exportedEntityType])?.toLowerCase();

    // make sure not to repeat the suffix in the entity name (keep the pluralized suffix)
    if (suffix && suffix === entityName) {
      entityName = null;
    }

    if (suffix) {
      suffix = pluralize.plural(suffix);
    }

    // create range suffixes for genome browser, not pluralized
    if (
      this.isGenomeBrowserContext() &&
      (this.props.currentContentSelection?.value.selectionType === SelectionType.SelectedRegion ||
        this.props.currentContentSelection?.value.selectionType === SelectionType.VisibleRegion ||
        // default case when nothing is selected in Genome Viewer
        !this.props.currentContentSelection)
    ) {
      const contig: Contig = this.props.genomeBrowser.contig;
      const range: Range = this.props.genomeBrowser.selectedRange || this.props.genomeBrowser.range;
      suffix = `${contig.accession}_${range.start}_${range.stop}`;
    }

    const setReverseString: string = this.props.currentExportReverseComplement ? "_rc" : "";
    const defaultFileName = `${entityName ?? ""}${entityName && suffix ? "_" : ""}${suffix ?? ""}${setReverseString}`;
    this.props.change(TableExportDialog.FIELD_EXPORT_FILENAME, sanitizeFilename(defaultFileName));
  }

  private checkExportTypeAndCount() {
    this.setState({ error: null, warning: null });
    const currentFormatSelection: ISelectOption = this.props.currentFormatSelection;
    if (currentFormatSelection && currentFormatSelection.value === EXPORT_FILE_FORMATS.FASTA_FORMAT) {
      const selectedEntityType: string = this.getSelectedEntityType();
      if (selectedEntityType === ENTITY_TYPE.ORGANISM) {
        const selectionValue = this.props.currentContentSelection.value;
        const selectionType = selectionValue.selectionType;
        const all: boolean = selectionType === SelectionType.AllRows;
        const count: number = selectionValue.itemCount;
        const res: IFastaCheck = { all: all, count: count };
        if (res.count > TableExportDialog.EXPORT_WARNING_THRESHOLD) {
          this.setState({ warning: "Exporting multiple genomes may take a long time." });
        }
        const tableData = selectionValue.tableData;

        this.props
          .checkSequencedOrganisms(getTableQuerySpecWithSelection(tableData))
          .then((sequenced) => {
            if (!sequenced) {
              const unSequencedGenomes = "Selection contains un-sequenced genomes.";
              const warning = this.state.warning ? unSequencedGenomes + " " + this.state.warning : unSequencedGenomes;
              this.setState({ warning: warning });
            }
          })
          .catch((error: Error) => {
            this.setState({ error: error.message });
          });
      }
    }
  }

  isGenomeBrowserContext = (): boolean => {
    return this.props.viewType === VIEW.GENOME_VIEW.type;
  };

  allowRegionExportFromBrowser = (): boolean => {
    return !!this.isGenomeBrowserContext();
  };

  getGffFeatureCount = (featureType: string) => {
    if (this.props.genome.isLoading || this.props.genome.genome == null) {
      return 0;
    }

    const genomeStatistics = this.props.genome.genome.genomeStatistics;

    switch (featureType) {
      case GFF_FEATURE_TYPE_GENE:
        return genomeStatistics.geneWithoutPseudogeneCount;
      case GFF_FEATURE_TYPE_MRNA:
        return genomeStatistics.mrnaCount;
      case GFF_FEATURE_TYPE_EXON:
        return genomeStatistics.exonCount;
      case GFF_FEATURE_TYPE_CDS:
        // CDS means exon in gff context, but protein in gb context
        return this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GBK_FORMAT
          ? genomeStatistics.proteinsCount
          : genomeStatistics.cdsCount;
      case GFF_FEATURE_TYPE_TRNA:
        return genomeStatistics.trnaCount;
      case GFF_FEATURE_TYPE_RRNA:
        return genomeStatistics.rrnaCount;
      case GFF_FEATURE_TYPE_SNORNA:
        return genomeStatistics.snornaCount;
      case GFF_FEATURE_TYPE_SNRNA:
        return genomeStatistics.snrnaCount;
      case GFF_FEATURE_TYPE_LNCRNA:
        return genomeStatistics.lncrnaCount;
      case GFF_FEATURE_TYPE_NCRNA:
        return genomeStatistics.ncrnaCount;
      case GFF_FEATURE_TYPE_PSEUDOGENE:
        return genomeStatistics.pseudogeneCount;
    }
  };

  createGffFeatureOptions = (): IGffTrackType[] => {
    const returnedFeatureOptions: IGffTrackType[] = [];
    if (this.isGenomeBrowserContext()) {
      // Genome Browser
      // if in genome browser, use the current track selection to pick the right gff feature types
      this.buildGffFeatureOptionsForGenomeViewer(returnedFeatureOptions);
    } else {
      // Summary page
      this.buildGffFeatureOptionsForSummaryPage(returnedFeatureOptions);
    }
    this.setState({ allFeatureOptions: returnedFeatureOptions });

    return returnedFeatureOptions;
  };

  private buildGffFeatureOptionsForGenomeViewer(featureOptions: IGffTrackType[]) {
    this.props.genomeBrowser.tracks
      .filter((t) => !t.hidden)
      .forEach((track) => {
        const matchingGffFeatureType: string = GFF_FEATURE_TYPES.find(
          (gffFeatureType: string) => !track.subTypeId && track.type === GFF_TO_BROWSER_FEATURE_TYPE[gffFeatureType],
        );
        if (matchingGffFeatureType) {
          featureOptions.push({
            name: matchingGffFeatureType,
            trackType: GFF_TO_EXPORT_TRACK_TYPE[matchingGffFeatureType],
          });
          // the track mixes the exotic RNAs among the other transcripts, so we have to enable them with the mRNAs
          if (matchingGffFeatureType === GFF_FEATURE_TYPE_MRNA) {
            const additionalTypes: IGffTrackType[] = [
              { name: GFF_FEATURE_TYPE_NCRNA, trackType: GFF_FEATURE_TYPE_NCRNA },
              { name: GFF_FEATURE_TYPE_LNCRNA, trackType: GFF_FEATURE_TYPE_LNCRNA },
              { name: GFF_FEATURE_TYPE_SNORNA, trackType: GFF_FEATURE_TYPE_SNORNA },
              { name: GFF_FEATURE_TYPE_SNRNA, trackType: GFF_FEATURE_TYPE_SNRNA },
              { name: GFF_FEATURE_TYPE_RRNA, trackType: GFF_FEATURE_TYPE_RRNA },
              { name: GFF_FEATURE_TYPE_TRNA, trackType: GFF_FEATURE_TYPE_TRNA },
            ];
            featureOptions.push(...additionalTypes);
          }
        } else if (track.subTypeId && (track.type === ENTITY_TYPE.AAFEATURE || track.type === ENTITY_TYPE.NUFEATURE)) {
          featureOptions.push({
            name: `${track.name} (${FEATURE_DISPLAY_NAME[track.type]})`,
            trackType: track.type,
            subTypeId: track.subTypeId,
          });
        }
      });
  }

  private buildGffFeatureOptionsForSummaryPage(featureOptions: IGffTrackType[]) {
    // if in summary page...
    GFF_FEATURE_TYPES.forEach((featureType: string) => {
      const featureCount: number = this.getGffFeatureCount(featureType);
      if (featureCount > 0) {
        featureOptions.push({
          name: `${featureType} (${featureCount})`,
          trackType: GFF_TO_EXPORT_TRACK_TYPE[featureType],
        });
      }
    });

    this.props.genome.genome.genomeStatistics.nuFeaturesCount?.forEach((value) => {
      if (value.count > 0) {
        const label: string = pluralizeWord(FEATURE_DISPLAY_NAME[ENTITY_TYPE.NUFEATURE], value.count);
        featureOptions.push({
          name: `${value.featureTypeName} (${value.count} ${label})`,
          trackType: ENTITY_TYPE.NUFEATURE,
          subTypeId: value.featureTypeId,
        });
      }
    });
  }

  createSourceOptions = (): ITableExportDialogSelectOption => {
    let optionList: ITableExportDialogSelectOption[] = [];
    let selectedRegionIndex: number = null;
    if (this.props.tables) {
      this.props.tables.forEach((table: ITableData) => {
        const trackOptions: ITableExportDialogSelectOption[] = this.createTableSourceOptions(
          table,
          this.isGenomeBrowserContext(),
        );
        optionList = optionList.concat(trackOptions);
      });
    }

    // conditionally add range option if exporting from the genome browser
    if (this.allowRegionExportFromBrowser()) {
      const contig: Contig = this.props.genomeBrowser.contig;

      if (this.props.genomeBrowser.selectedRange) {
        const selectedRange: Range = this.props.genomeBrowser.selectedRange;

        optionList.push({
          value: {
            selectionType: SelectionType.SelectedRegion,
          },
          label: `Selected Region (Bases ${selectedRange.start}-${selectedRange.stop} of ${contig.accession})`,
        });
        selectedRegionIndex = optionList.length - 1;
      } else {
        const visibleRange: Range = this.props.genomeBrowser.range;
        optionList.push({
          value: {
            selectionType: SelectionType.VisibleRegion,
          },
          label: `Visible Region (Bases ${visibleRange.start}-${visibleRange.stop} of ${contig.accession})`,
        });
      }
    }

    if (this.props.summaryPageEntity) {
      optionList.push({
        value: {
          selectionType: SelectionType.AllRows,
          itemCount: 1,
        },
        label: `1 ${FEATURE_DISPLAY_NAME[this.props.summaryPageEntity.groupType]}`,
      });
    }

    // The stored value includes the label.
    // When we want to change the label robustly, we need to exchange it as well in the redux form store.
    const newSourceOption = optionList.find(
      (o) =>
        o.value.selectionType === this.props.currentContentSelection?.value.selectionType &&
        o.value.tableData === this.props.currentContentSelection?.value.tableData,
    );
    this.props.change(TableExportDialog.FIELD_EXPORT_SELECTOR, newSourceOption);

    this.setState({ sourceOptions: optionList });

    // We default on the selected region export if a region is selected in the genome browser,
    // otherwise we default on the first option.
    return this.allowRegionExportFromBrowser() && this.props.genomeBrowser.selectedRange
      ? optionList[selectedRegionIndex]
      : optionList[0];
  };

  createTableSourceOptions = (
    tableData: ITableData,
    isGenomeBrowserContext?: boolean,
  ): ITableExportDialogSelectOption[] => {
    const optionList: ITableExportDialogSelectOption[] = [];
    const selectedItemCount = tableData.selectionModel.selectedIds.length;
    const typeDisplayName: string = getSequenceFeatureTypeDisplayName(
      tableData.tableQuerySpec,
      this.props.sequenceFeatureTypes,
    );

    const isExcelFormat = this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.XLSX_FORMAT;
    // when possible, do not use the entity-specific, but a generic label for the table export options to avoid confusion
    // (not possible for genome viewer where multiple tables may be offered and must be distinguished)
    const useGenericLabel: boolean = isExcelFormat && !isGenomeBrowserContext;
    const totalCount = tableData.filteredRowCount;
    const groupLabel = capitalizeFirstLetterOfAllWords(useGenericLabel ? "Table" : typeDisplayName);

    if (selectedItemCount > 0) {
      const itemsLabel = pluralizeWord(useGenericLabel ? "row" : typeDisplayName, selectedItemCount);
      optionList.push({
        value: {
          selectionType: SelectionType.SelectedRows,
          itemCount: selectedItemCount,
          tableData: tableData,
        },
        label: `${groupLabel} Export - ${selectedItemCount} selected ${itemsLabel}`,
      });
    }

    // omit full export when in Genome Browser context
    if (totalCount > 0 && tableData && !isGenomeBrowserContext) {
      const itemsLabel = pluralizeWord(useGenericLabel ? "row" : typeDisplayName, totalCount);
      optionList.push({
        value: {
          selectionType: SelectionType.AllRows,
          itemCount: totalCount,
          tableData: tableData,
        },
        label:
          totalCount === 1
            ? `${groupLabel} Export - 1 ${itemsLabel}`
            : `${groupLabel} Export - All ${totalCount} ${itemsLabel}`,
      });
    }

    return optionList;
  };

  isSequenceExportable = (
    selectedContentOption: ITableExportDialogSelectOption,
    selectedEntityType: string,
  ): boolean => {
    if (!selectedContentOption) {
      return;
    }

    const selectedParentType: string =
      !!selectedContentOption.value.tableData && selectedContentOption.value.tableData.tableQuerySpec.queryType;
    // decide which data entity should be fitted with sequence export formats
    return (
      selectedContentOption.value.selectionType === SelectionType.SelectedRegion ||
      selectedContentOption.value.selectionType === SelectionType.VisibleRegion ||
      selectedEntityType === ENTITY_TYPE.TRANSCRIPT ||
      selectedEntityType === ENTITY_TYPE.TRANSCRIPT_LOCATION ||
      selectedEntityType === ENTITY_TYPE.PROTEIN ||
      selectedEntityType === ENTITY_TYPE.PROTEIN_LOCATION ||
      (selectedEntityType === ENTITY_TYPE.PROTEIN_MUTATION && selectedParentType === ENTITY_TYPE.MUTATION) ||
      (selectedEntityType === ENTITY_TYPE.GENE_MUTATION && selectedParentType === ENTITY_TYPE.MUTATION) ||
      selectedEntityType === ENTITY_TYPE.GENE ||
      selectedEntityType === ENTITY_TYPE.CONTIG ||
      selectedEntityType === ENTITY_TYPE.ORGANISM ||
      selectedEntityType === ENTITY_TYPE.ORTHOLOG ||
      selectedEntityType === ENTITY_TYPE.DIRECTIONAL_ORTHOLOG ||
      selectedEntityType === ENTITY_TYPE.AAFEATURE ||
      selectedEntityType === ENTITY_TYPE.NUFEATURE ||
      selectedEntityType === ENTITY_TYPE.PROBE ||
      selectedEntityType === ENTITY_TYPE.OTHER ||
      selectedEntityType === ENTITY_TYPE.BLAST_HIT
    );
  };

  isReverseComplementExportable = (): boolean => {
    const selectedContentOption: ITableExportDialogSelectOption = this.props.currentContentSelection;

    if (!selectedContentOption) {
      return;
    }

    const selectedEntityType = this.getSelectedEntityType();

    const selectedParentType: string =
      !!selectedContentOption.value.tableData && selectedContentOption.value.tableData.tableQuerySpec.queryType;
    // decide which data entity should be fitted with sequence export formats
    return (
      selectedContentOption.value.selectionType === SelectionType.SelectedRegion ||
      selectedContentOption.value.selectionType === SelectionType.VisibleRegion ||
      selectedEntityType === ENTITY_TYPE.TRANSCRIPT ||
      selectedEntityType === ENTITY_TYPE.TRANSCRIPT_LOCATION ||
      (selectedEntityType === ENTITY_TYPE.GENE_MUTATION && selectedParentType === ENTITY_TYPE.MUTATION) ||
      selectedEntityType === ENTITY_TYPE.GENE ||
      selectedEntityType === ENTITY_TYPE.CONTIG ||
      selectedEntityType === ENTITY_TYPE.ORGANISM ||
      selectedEntityType === ENTITY_TYPE.ORTHOLOG ||
      selectedEntityType === ENTITY_TYPE.DIRECTIONAL_ORTHOLOG ||
      selectedEntityType === ENTITY_TYPE.NUFEATURE ||
      selectedEntityType === ENTITY_TYPE.PROBE ||
      selectedEntityType === ENTITY_TYPE.OTHER
    );
  };

  getSelectedEntityType = () => {
    const selectedContentOption: ITableExportDialogSelectOption = this.props.currentContentSelection;
    return this.props.summaryPageEntity
      ? this.props.summaryPageEntity.groupType
      : selectedContentOption?.value.tableData?.tableQuerySpec.resultType;
  };

  isGenbankExportAvailable = () => {
    // the Genbank export is available for genome viewer (visible/selected region) and genome summary pages
    const currSelectionType: number =
      this.props.currentContentSelection && this.props.currentContentSelection.value.selectionType;

    return (
      (this.isGenomeBrowserContext() &&
        (currSelectionType === SelectionType.SelectedRegion || currSelectionType === SelectionType.VisibleRegion)) ||
      (this.props.summaryPageEntity && this.props.summaryPageEntity.groupType === ENTITY_TYPE.ORGANISM)
    );
  };
  isGffExportAvailable = () => {
    // the gff export is only available for summary display - SEL6-2381
    // BUT CURRENTLY NOT for genome viewer if selected region or current view is chosen - SEL7-627
    // The same applies to the Genbank Export.
    return this.props.summaryPageEntity && this.props.summaryPageEntity.groupType === ENTITY_TYPE.ORGANISM;
  };

  createFormatOptions = (): ISelectOption[] => {
    const props: ITableExportDialogProps = this.props;

    const selectedContentOption: ITableExportDialogSelectOption = props.currentContentSelection;
    if (!selectedContentOption) {
      return;
    }
    const selectedEntityType: string = this.getSelectedEntityType();

    // decide which data entity should be fitted with sequence export formats
    const isSequenceExportable: boolean = this.isSequenceExportable(selectedContentOption, selectedEntityType);

    // generic export format options (always available)
    const formatOptions: ISelectOption[] = [];

    // add the excel format option only if no region export was selected, and we are not on a summary page
    if (
      !(
        selectedContentOption.value.selectionType === SelectionType.SelectedRegion ||
        selectedContentOption.value.selectionType === SelectionType.VisibleRegion ||
        props.summaryPageEntity
      )
    ) {
      formatOptions.push({ label: "Excel (.xlsx)", value: EXPORT_FILE_FORMATS.XLSX_FORMAT });

      if (selectedEntityType === ENTITY_TYPE.SAMPLE) {
        formatOptions.push({
          label: "Analyst Column Annotation (.gdc)",
          value: EXPORT_FILE_FORMATS.GDC_FORMAT,
        });
      }
    }

    if (isSequenceExportable) {
      formatOptions.push({ label: "FASTA Sequence (with headers)", value: EXPORT_FILE_FORMATS.FASTA_FORMAT });
    }

    if (this.isGffExportAvailable()) {
      formatOptions.push({ label: "GFF annotations", value: EXPORT_FILE_FORMATS.GFF_FORMAT });
    }
    if (this.isGenbankExportAvailable()) {
      formatOptions.push({ label: "GenBank annotations", value: EXPORT_FILE_FORMATS.GBK_FORMAT });
    }

    if (
      isSequenceExportable &&
      selectedEntityType !== ENTITY_TYPE.ORGANISM &&
      (selectedContentOption.value.selectionType === SelectionType.SelectedRegion ||
        selectedContentOption.value.selectionType === SelectionType.VisibleRegion ||
        selectedContentOption.value.itemCount === 1)
    ) {
      formatOptions.push({ label: "Raw Sequence (without headers)", value: EXPORT_FILE_FORMATS.RAW_FORMAT });
    }

    if (this.props.viewType === VIEW.TABLE_VIEW.type && selectedEntityType === ENTITY_TYPE.VCF_VARIANT) {
      formatOptions.push({ label: "Variant Call Format (.vcf)", value: EXPORT_FILE_FORMATS.VCF_FORMAT });
    }

    this.setState({ formatOptions: formatOptions });
    return formatOptions;
  };

  handleExportToFile = (values): Promise<any> => {
    this.setState({ action: EXPORT_ACTION.TO_FILE });
    return this.handleExport(values, false);
  };

  handleExportToClipboard = (values): Promise<any> => {
    this.setState({ action: EXPORT_ACTION.TO_CLIPBOARD });
    return this.handleExport(values, true);
  };

  getGenomeBrowserRegion = (selectionType: SelectionType): InputRegionInput => {
    return {
      contigId: this.props.genomeBrowser.contig.id,
      range:
        selectionType === SelectionType.SelectedRegion
          ? this.props.genomeBrowser.selectedRange
          : this.props.genomeBrowser.range,
      strand: STRAND_TYPE.PLUS,
    };
  };

  private getExportPayload = (values, toClipboard: boolean) => {
    const format: ISelectOption = values[TableExportDialog.FIELD_EXPORT_FORMAT];
    const content: ITableExportDialogSelectOption = values[TableExportDialog.FIELD_EXPORT_SELECTOR];
    const filename: string = values[TableExportDialog.FIELD_EXPORT_FILENAME]?.trim();
    const fastaHeaderTemplate: ISelectOption = values[TableExportDialog.FIELD_EXPORT_FASTA_HEADER];
    const gffTrackTypes: IGffTrackType[] = values[TableExportDialog.FIELD_GFF_FEATURE_TYPES];
    const gffLandmarkName: ISelectOption = values[TableExportDialog.FIELD_GFF_LANDMARK_NAME];
    const gffWithSequence: number = values[TableExportDialog.FIELD_GFF_WITH_SEQUENCE];
    const compressed: boolean = values[TableExportDialog.FIELD_EXPORT_COMPRESSED];
    const reverseComplement: boolean = values[TableExportDialog.FIELD_EXPORT_REVERSE_COMPLEMENT];

    let genericExportPayload: IGenericExportPayload;

    // encapsulated preliminary gff export logic
    if (format.value === EXPORT_FILE_FORMATS.GFF_FORMAT || format.value === EXPORT_FILE_FORMATS.GBK_FORMAT) {
      genericExportPayload = {
        // format: EXPORT_FILE_FORMATS.GFF_FORMAT,
        format: format.value,
        genome: this.isGenomeBrowserContext() ? this.props.genomeBrowser.genome : this.props.genome.genome,
        name: filename,
        trackTypes: gffTrackTypes,
        useChromosomeName: !!gffLandmarkName.value,
        withSequence: !!gffWithSequence,
        compressed: compressed,
        reverseComplement: reverseComplement,
        region: this.isGenomeBrowserContext() ? this.getGenomeBrowserRegion(content.value.selectionType) : null,
      };
    } else if (format.value === EXPORT_FILE_FORMATS.VCF_FORMAT) {
      genericExportPayload = {
        genome: this.isGenomeBrowserContext() ? this.props.genomeBrowser.genome : this.props.genome.genome,
        entityName: FEATURE_DISPLAY_NAME[ENTITY_TYPE.VCF_VARIANT],
        format: format.value,
        selectionType: content.value.selectionType,
        table: content.value.tableData,
        region: null,
        summaryPageEntity: this.props.summaryPageEntity,
        toClipboard,
        filename: filename,
        fastaHeaderTemplate: fastaHeaderTemplate.value,
        compressed: !toClipboard && compressed,
        reverseComplement: reverseComplement,
      };
    } else {
      let region: InputRegionInput = null;

      if (
        this.isGenomeBrowserContext() &&
        (content.value.selectionType === SelectionType.SelectedRegion ||
          content.value.selectionType === SelectionType.VisibleRegion)
      ) {
        // payload is not required, but provide the region details
        region = this.getGenomeBrowserRegion(content.value.selectionType);
      }

      const displayName: string = getSequenceFeatureTypeDisplayName(
        content.value.tableData?.tableQuerySpec,
        this.props.sequenceFeatureTypes,
      );
      genericExportPayload = {
        entityName:
          displayName && pluralizeWord(displayName, content.value.tableData.selectionModel.selectedIds.length),
        format: format.value,
        selectionType: content.value.selectionType,
        table: content.value.tableData,
        region: region,
        summaryPageEntity: this.props.summaryPageEntity,
        toClipboard,
        filename: filename,
        fastaHeaderTemplate: fastaHeaderTemplate.value,
        compressed: !toClipboard && compressed,
        reverseComplement: reverseComplement,
      };
    }

    return genericExportPayload;
  };

  handleExport = (values, toClipboard: boolean): Promise<any> => {
    const genericExportPayload = this.getExportPayload(values, toClipboard);

    return this.props.exportItemsOrRegion(genericExportPayload).then(
      (fulfilled) => {
        // make sure the dialog is closed, in production mode, this does not work sometimes without the timeout
        if (!toClipboard)
          setTimeout(() => {
            this.props.close?.();
            this.props.reset();
            this.props.destroy();
          }, 100);
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  isExportButtonDisabled = () =>
    ((this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GFF_FORMAT ||
      this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GBK_FORMAT) &&
      this.props.genome.isLoadingGffFeatureStatistics) ||
    this.props.submitting ||
    !this.props.initialized ||
    !this.props.valid;

  copyToClipboardDisabled = () => {
    return (
      this.props.submitting ||
      !this.props.initialized ||
      !this.props.valid ||
      (this.props.currentFormatSelection &&
        (this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.XLSX_FORMAT ||
          this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.GDC_FORMAT ||
          this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.GBK_FORMAT ||
          this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.GFF_FORMAT ||
          this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.VCF_FORMAT))
    );
  };

  ensureGffFeatureAbsent = (list: IGffTrackType[], keys: string[]): void => {
    keys.forEach((k: string) => {
      remove(list, (f: IGffTrackType) => f.trackType === k);
    });
  };

  ensureGffFeaturePresent = (list: IGffTrackType[], keys: string[], allOptions: IGffTrackType[]): void => {
    keys.forEach((k: string) => {
      if (!list.find((f: IGffTrackType) => f.trackType === k)) {
        // check if the full option list contains the item, and add it to the selection if so...
        const itemToAdd: IGffTrackType = allOptions.find((f: IGffTrackType) => f.trackType === k); // this works because the track type is sufficient to identify everything that we have to force "on"
        if (itemToAdd) {
          list.push(itemToAdd);
        }
      }
    });
  };

  ensureGffFeatureConsistency = (previousSelection: IGffTrackType[], newSelection: IGffTrackType[]): void => {
    const itemChanged: boolean = newSelection.length !== previousSelection.length;
    const itemAdded: boolean = newSelection.length > previousSelection.length;

    const iteratee = (value: IGffTrackType) => `${value.trackType}-${value.subTypeId}`;
    const diffElements: IGffTrackType[] = itemChanged
      ? itemAdded
        ? differenceBy(newSelection, previousSelection, iteratee)
        : differenceBy(previousSelection, newSelection, iteratee)
      : [];

    if (diffElements.length === 1 && !!diffElements[0]) {
      const diffKey: string = diffElements[0].trackType;

      const newFeatureTypeSelection: IGffTrackType[] = newSelection.slice(0);
      switch (diffKey) {
        case ENTITY_TYPE.GENE:
          if (!itemAdded) {
            this.ensureGffFeatureAbsent(newFeatureTypeSelection, [
              GFF_FEATURE_TYPE_MRNA,
              ENTITY_TYPE.NUFEATURE,
              ENTITY_TYPE.PROTEIN,
              ENTITY_TYPE.AAFEATURE,
              GFF_FEATURE_TYPE_RRNA,
              GFF_FEATURE_TYPE_TRNA,
              GFF_FEATURE_TYPE_EXON,
              GFF_FEATURE_TYPE_SNORNA,
              GFF_FEATURE_TYPE_SNRNA,
              GFF_FEATURE_TYPE_LNCRNA,
              GFF_FEATURE_TYPE_NCRNA,
            ]);
          }
          break;
        case GFF_FEATURE_TYPE_MRNA:
        case ENTITY_TYPE.PROTEIN:
        case GFF_FEATURE_TYPE_EXON:
        case ENTITY_TYPE.NUFEATURE:
        case GFF_FEATURE_TYPE_RRNA:
        case GFF_FEATURE_TYPE_TRNA:
        case GFF_FEATURE_TYPE_SNORNA:
        case GFF_FEATURE_TYPE_SNRNA:
        case GFF_FEATURE_TYPE_LNCRNA:
        case GFF_FEATURE_TYPE_NCRNA:
          if (itemAdded) {
            this.ensureGffFeaturePresent(newFeatureTypeSelection, [ENTITY_TYPE.GENE], this.state.featureOptions);
          }
          break;
        case ENTITY_TYPE.AAFEATURE:
          if (itemAdded) {
            this.ensureGffFeaturePresent(
              newFeatureTypeSelection,
              [ENTITY_TYPE.PROTEIN, ENTITY_TYPE.GENE],
              this.state.featureOptions,
            );
          }
          break;
      }

      this.props.change(TableExportDialog.FIELD_GFF_FEATURE_TYPES, newFeatureTypeSelection);
    }
  };

  getFastaFileExtension = (entityType: string): string => {
    if (entityType === ENTITY_TYPE.BLAST_HIT && this.props.job.job.tool === BLAST_METHOD.BLASTP) {
      return ".faa";
    }

    switch (entityType) {
      case ENTITY_TYPE.PROTEIN:
      case ENTITY_TYPE.PROTEIN_LOCATION:
      case ENTITY_TYPE.AAFEATURE:
      case ENTITY_TYPE.ORTHOLOG:
      case ENTITY_TYPE.DIRECTIONAL_ORTHOLOG:
      case ENTITY_TYPE.PROTEIN_MUTATION:
        return ".faa";
      default:
        return ".fa";
    }
  };

  private formatChanged = (value: ValueType<any, false>, _action: ActionMeta<any>) => {
    // Make the "Contig Accession ID" the default if we export GFF: SEL6-3185
    if (value.value === EXPORT_FILE_FORMATS.GFF_FORMAT || value.value === EXPORT_FILE_FORMATS.GBK_FORMAT) {
      this.props.change(TableExportDialog.FIELD_GFF_LANDMARK_NAME, CONTIG_ID);
    }
    this.updateFeatureTypeOptions(value.value, this.state.allFeatureOptions.slice(0));
  };

  private updateFeatureTypeOptions(value: string /* EXPORT_FILE_FORMATS */, allFeatureOptions: IGffTrackType[]) {
    if (value === EXPORT_FILE_FORMATS.GFF_FORMAT) {
      const filteredOptions = allFeatureOptions;
      this.props.change(TableExportDialog.FIELD_GFF_FEATURE_TYPES, filteredOptions);
      this.setState({ featureOptions: filteredOptions });
    }
    if (value === EXPORT_FILE_FORMATS.GBK_FORMAT) {
      const filteredOptions = allFeatureOptions
        .filter((f) => f.trackType !== GFF_FEATURE_TYPE_EXON)
        .filter((f) => f.trackType !== GFF_FEATURE_TYPE_AA_FEATURE);
      this.props.change(TableExportDialog.FIELD_GFF_FEATURE_TYPES, filteredOptions);
      this.setState({ featureOptions: filteredOptions });
    }
  }

  private withSequenceChanged = (_event, newValue: boolean): void => {
    if (newValue) {
      // Only "Contig Accession ID" possible: SEL6-3185
      this.props.change(TableExportDialog.FIELD_GFF_LANDMARK_NAME, CONTIG_ID);
    }
    this.setState({ withSequence: newValue });
  };

  render() {
    // In the content browser we are exporting contig data, but the table shows genomes. FASTA can't use the column headers.
    const contentBrowserView: boolean = this.props.viewType === VIEW.CONTENT_BROWSER_VIEW.type;
    const currSelectionType: number =
      this.props.currentContentSelection && this.props.currentContentSelection.value.selectionType;
    const regionSourceSelected: boolean =
      currSelectionType === SelectionType.SelectedRegion || currSelectionType === SelectionType.VisibleRegion;
    const FASTAFormatSelected: boolean =
      this.props.currentFormatSelection && this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.FASTA_FORMAT;
    const RAWFormatSelected: boolean =
      this.props.currentFormatSelection && this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.RAW_FORMAT;
    const GFFFormatSelected: boolean =
      this.props.currentFormatSelection && this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.GFF_FORMAT;
    const GBKFormatSelected: boolean =
      this.props.currentFormatSelection && this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.GBK_FORMAT;
    const GDCFormatSelected: boolean =
      this.props.currentFormatSelection && this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.GDC_FORMAT;
    const ExcelFormatSelected: boolean =
      this.props.currentFormatSelection && this.props.currentFormatSelection.value === EXPORT_FILE_FORMATS.XLSX_FORMAT;
    const FASTAHeaderTemplateOptions: ISelectOption[] = FASTAFormatSelected && [
      FASTA_HEADER_TEMPLATE_OPTIONS.PROFILER_IDENTIFIERS,
      FASTA_HEADER_TEMPLATE_OPTIONS.SELECTOR_IDENTIFIERS,
    ];
    if (FASTAFormatSelected && !this.props.summaryPageEntity && !regionSourceSelected && !contentBrowserView) {
      FASTAHeaderTemplateOptions.push(FASTA_HEADER_TEMPLATE_OPTIONS.VISIBLE_COLUMNS);
    }

    // Caution: This is fragile logic which handles exotic situations like "visible region" only implicitly(by ignoring them and returning null).
    // There seems to be no generic way to detect the exported entity type in all situations because sometimes there isn't any.
    // We assume that in this case, a nucleic acid export will happen. This seems to work for all cases.

    const currentFormatSelectionValue = this.props.currentFormatSelection?.value;
    let fileExtension = ".txt";

    if (currentFormatSelectionValue) {
      if (currentFormatSelectionValue === EXPORT_FILE_FORMATS.FASTA_FORMAT) {
        fileExtension = this.getFastaFileExtension(this.props.exportedEntityType);
      } else {
        fileExtension = `.${currentFormatSelectionValue}`;
      }
    }

    if (!GDCFormatSelected && !ExcelFormatSelected && this.props.currentExportCompressed) {
      fileExtension += ".gz";
    }

    return (
      <Dialog
        closeFn={this.props.close}
        mainActionButtonProps={{
          isLoading: this.props.submitting && this.state.action === EXPORT_ACTION.TO_FILE,
          isDisabled: this.isExportButtonDisabled(),
          children: "Export",
          onPress: this.props.handleSubmit(this.handleExportToFile),
        }}
        overWriteMaxHeight
        dontUseSecondAction={
          this.copyToClipboardDisabled() ? undefined : (
            <Copyable
              getTextContent={this.state.getContent}
              title={(isSecure) => (isSecure ? "Copy to Clipboard" : "Focus and Copy to Clipboard")}
              hideContent={(isSecure) => isSecure}
              isDisabled={
                this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GFF_FORMAT &&
                this.props.currentFormatSelection?.value === EXPORT_FILE_FORMATS.GBK_FORMAT &&
                this.props.genome.isLoadingGffFeatureStatistics
              }
              onCopy={(content) => {
                if (content.endsWith("...")) {
                  notify.warning({
                    title: selMessage({ id: "export.sequences.warningTitle" }),
                    children: selMessage({
                      id: "export.sequences.warningMessage",
                      values: { maxExportLength: MAX_SEQUENCE_EXPORT_LENGTH },
                    }),
                    delay: 2000,
                  });
                }
              }}
            />
          )
        }
        footerMessage={{
          message: this.props.error || this.state.error || this.state.warning,
          intent: this.props.error || this.state.error ? CalloutIntent.Danger : CalloutIntent.Warning,
        }}
      >
        <ReactSelectReduxFormField
          name={TableExportDialog.FIELD_EXPORT_SELECTOR}
          label="Content"
          options={this.state.sourceOptions}
          component={ReactSelectField}
          isClearable={false}
          isSearchable={false}
        />

        <ReactSelectReduxFormField
          name={TableExportDialog.FIELD_EXPORT_FORMAT}
          label="Format"
          placeholder="Please select..."
          options={this.state.formatOptions}
          component={ReactSelectField}
          isClearable={false}
          isSearchable={false}
          onChange={this.formatChanged}
        />

        {FASTAFormatSelected && (
          <ReactSelectReduxFormField
            name={TableExportDialog.FIELD_EXPORT_FASTA_HEADER}
            label="FASTA header template"
            placeholder="Please select..."
            options={FASTAHeaderTemplateOptions}
            component={ReactSelectField}
            isClearable={false}
            isSearchable={false}
            validate={simpleDropdownFieldValidation}
          />
        )}

        <div
          style={{
            display: "flex",
            columnGap: "5px",
            width: "100%",
          }}
        >
          {!GDCFormatSelected && !ExcelFormatSelected && (
            <CheckboxReduxFormField
              name={TableExportDialog.FIELD_EXPORT_COMPRESSED}
              label="Compressed"
              tooltipText="Compressed format to reduce download time."
              component={CheckboxField}
            />
          )}

          {(FASTAFormatSelected || RAWFormatSelected) && this.isReverseComplementExportable() && (
            <CheckboxReduxFormField
              name={TableExportDialog.FIELD_EXPORT_REVERSE_COMPLEMENT}
              label="Reverse complement"
              tooltipText="Reverse complement of the sequence."
              component={CheckboxField}
            />
          )}
        </div>

        <InputReduxFormField
          componentClass="input"
          name={TableExportDialog.FIELD_EXPORT_FILENAME}
          label={"File name"}
          placeholder={`Please provide a name for the file to be downloaded`}
          component={InputField}
          validate={[fileNameFieldValidation, simpleFieldValidation]}
          suffix={<Addon label={fileExtension} />}
        />

        {(GFFFormatSelected || GBKFormatSelected) && (
          <CheckboxSelectReduxFormField
            name={TableExportDialog.FIELD_GFF_FEATURE_TYPES}
            uniqueKey={TableExportDialog.FIELD_GFF_FEATURE_TYPES}
            title="Feature Types"
            placeholder={this.props.genome.isLoadingGffFeatureStatistics ? "Loading..." : "Please select..."}
            labelKey={"name"}
            valueKey={"name"}
            options={this.state.featureOptions}
            component={CheckboxSelectField}
            showFilter={true}
            showSelectAll={true}
            renderItemLabel={true}
            numberDisplayed={0}
            key={"gffFeatures"}
          />
        )}

        {GFFFormatSelected && (
          <>
            <ReactSelectReduxFormField
              name={TableExportDialog.FIELD_GFF_LANDMARK_NAME}
              label="Contig ID"
              placeholder="Please select..."
              options={gffLandmarkNameOptions}
              component={ReactSelectField}
              isClearable={false}
              isSearchable={false}
              isDisabled={this.state.withSequence}
              key={"gffLandmarkName"}
            />

            <CheckboxReduxFormField
              name={TableExportDialog.FIELD_GFF_WITH_SEQUENCE}
              label="Include genome sequence"
              tooltipText="Include FASTA-formatted contig sequences in the GFF file."
              component={CheckboxField}
              onChange={this.withSequenceChanged}
              key={"gffIncludeSequence"}
            />
          </>
        )}
      </Dialog>
    );
  }
}

const _component = connect<
  ITableExportDialogAppStateProps,
  ITableExportDialogDispatchProps,
  ITableExportDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: TableExportDialog.FORM_NAME,
  })(TableExportDialog) as any,
);

export { _component as TableExportDialog };
